import { IconAlertTriangle } from "@tabler/icons-react";
import { Button } from "../../../../components/common/button";
import { Dialog } from "../../../../components/common/dialog";
import { Text } from "../../../../components/common/typography";
import type { ProductLifecycleAction } from "../../hooks/useProductLifecycleActions";

interface ProductStatusDialogProps {
  open: boolean;
  action: ProductLifecycleAction | null;
  productName: string;
  isSaving?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

function titleForAction(action: ProductLifecycleAction | null): string {
  if (action === "discontinued") return "Discontinue product?";
  if (action === "reactivate") return "Reactivate product?";
  return "Set product inactive?";
}

function bodyForAction(action: ProductLifecycleAction | null, productName: string): string {
  if (action === "discontinued") {
    return `This will discontinue "${productName}" and hide it from buyers.`;
  }
  if (action === "reactivate") {
    return `This will make "${productName}" visible to buyers again.`;
  }
  return `This will set "${productName}" to inactive and hide it from buyers.`;
}

function confirmLabel(action: ProductLifecycleAction | null): string {
  if (action === "discontinued") return "Discontinue";
  if (action === "reactivate") return "Reactivate";
  return "Set inactive";
}

export function ProductStatusDialog({
  open,
  action,
  productName,
  isSaving = false,
  onConfirm,
  onClose,
}: ProductStatusDialogProps) {
  return (
    <Dialog
      open={open}
      setOpen={(next) => {
        if (!next) onClose();
      }}
      titleIcon={<IconAlertTriangle size={24} className="text-accentError" />}
      title={titleForAction(action)}
      titleAlignment="left"
      showButtons={false}
      width="max-w-xl"
      content={
        <div className="mt-2 flex flex-col gap-5">
          <Text variant="body" tone="body">
            {bodyForAction(action, productName)}
          </Text>

          <div className="flex flex-col gap-3">
            <Button
              variant={action === "discontinued" ? "secondaryDanger" : "primary"}
              onClick={onConfirm}
              disabled={isSaving || action === null}
              fullWidth
            >
              {isSaving ? "Saving..." : confirmLabel(action)}
            </Button>
            <Button variant="secondary" onClick={onClose} disabled={isSaving} fullWidth>
              Cancel
            </Button>
          </div>
        </div>
      }
    />
  );
}
